// Splits collections into "Games", "Tokens" and "Non-Games" for the
// Marketplace tabs.
//
// There is no official genre/category field anywhere in the AtomicAssets
// protocol — verified live against the API (Sept 2026): a collection's
// `data` carries only name/description/image/socials/creator_info, nothing
// resembling a tag or genre. So this is a best-effort heuristic, not a
// lookup: match the collection's display name and account-name slug
// against keyword lists, and fall through to "non-games" when nothing
// matches.
//
// "Tokens" exists because some collections mint NFTs that are really just
// certificates *representing* a fungible token (e.g. an asset literally
// named "SKATE Token", collection `skatecoinwax` / display "sk8coin") —
// those aren't game items or art/collectibles, they're closer to a coin,
// so they get pulled into their own bucket rather than landing in either
// Games or Non-Games by accident.
//
// Checked in order: overrides, then TOKEN_KEYWORDS, then GAME_KEYWORDS,
// else "non-games". Tokens is checked before Games deliberately — a
// collection that explicitly calls itself a coin/token is more specifically
// a token than a game, even if it also uses game-ish words elsewhere in its
// name (e.g. a play-to-earn coin). Both keyword lists are one-directional
// (no mirrored "non-games" list) — a sports-card or toy-line collection
// landing in Non-Games by default is a much smaller miss than a real game
// or token slipping in with the Topps/Funko crowd.
export type CollectionCategory = "games" | "tokens" | "non-games";

// Small, hand-maintained corrections for specific collections the
// automatic classifier below gets wrong — always checked first and always
// wins over the heuristic. Keyed by the real on-chain `collection_name`
// (verified live against the AtomicAssets API, not just guessed from the
// display name shown on-site, which can differ from the slug — e.g.
// "Splinterlands" is stored as the 12-character `splintrlands`).
//
// To fix a miscategorized collection, add its collection_name here — you
// can read it off the asset/collection URL on this site, or from
// https://wax.api.atomicassets.io/atomicassets/v1/collections?match=<name>.
const CATEGORY_OVERRIDES: Record<string, CollectionCategory> = {
  "alien.worlds": "games",
  farmersworld: "games",
  farmingtales: "games",
  rplanet: "games",
  dungeonitems: "games",
  bcbrawlers: "games",
  "stf.capcom": "games",
  colonizemars: "games",
  splintrlands: "games",
  // Verified live (Sept 2026): asset 1099949338202 "SKATE Token", schema
  // crypto.clips, description "SKATE TOKEN to be used for in game
  // purchases, Blends and unlock special web pages" — an NFT certificate
  // for a fungible token, not a game item itself.
  skatecoinwax: "tokens",
};

// Substring keywords (case-insensitive), matched at a word boundary, same
// convention as GAME_KEYWORDS below. Deliberately narrow — "coin"/"token"
// are common enough words that a wide net would misfire on collectibles
// that just happen to use them figuratively, so this only covers the
// clearest crypto/currency naming patterns.
const TOKEN_KEYWORDS = [
  "token",
  "coin",
  "cryptocurrency",
  "crypto currency",
  "currency",
];

const TOKEN_KEYWORD_RE = new RegExp(`\\b(${TOKEN_KEYWORDS.join("|")})`, "i");

// Substring keywords (case-insensitive), matched at a word boundary so
// "war" doesn't fire on "hardware" but still matches inside "warriors" —
// checked against the collection's display name and its account-name slug
// together, so e.g. "Alien Worlds" matches via "world" even though the
// slug alone (alien.worlds) wouldn't need it.
const GAME_KEYWORDS = [
  "game",
  "gaming",
  "rpg",
  "mmorpg",
  "quest",
  "battle",
  "brawl",
  "fight",
  "war",
  "siege",
  "conquest",
  "defense",
  "defence",
  "dungeon",
  "adventure",
  "hero",
  "weapon",
  "combat",
  "metaverse",
  "arena",
  "guild",
  "strategy",
  "tycoon",
  "raid",
  "loot",
  "dragon",
  "monster",
  "mining",
  "miner",
  "craft",
  "farm",
  "land",
  "world",
  "realm",
  "kingdom",
  "empire",
  "planet",
  "colonize",
  "galaxy",
  "galactic",
  "survival",
  "pvp",
  "pve",
  "idle",
  "clicker",
  "simulator",
  "fortress",
  "castle",
  "legion",
  "saga",
  "chronicles",
  "odyssey",
  "rebellion",
  "p2e",
];

const GAME_KEYWORD_RE = new RegExp(`\\b(${GAME_KEYWORDS.join("|")})`, "i");

export function classifyCollection(collectionName: string, displayName?: string): CollectionCategory {
  const override = CATEGORY_OVERRIDES[collectionName];
  if (override) return override;
  const haystack = `${displayName ?? ""} ${collectionName}`;
  if (TOKEN_KEYWORD_RE.test(haystack)) return "tokens";
  return GAME_KEYWORD_RE.test(haystack) ? "games" : "non-games";
}
